// Tutorials — content only. Kept plain so children can read the copy aloud
// and the whole tour stays credit-cheap (no external components, no LLM calls
// unless the user explicitly asks Vaelithon Reign to customize a step).
//
// Two tours:
//   `tour_general` — how to use Aurethion Crown day to day (any admin).
//   `tour_land_sale` — private playbook for RSVP's own land sales, with the
//     legal / compliance paperwork compartmentalized step by step.  Marked
//     `ownerOnly` so only Owner CEO sees it in the drawer.
//
// Each step:
//   title, why, do (bullets), nav (route to jump to), highlightTestId (optional
//   data-testid to ring on the target page).

export const TUTORIALS = [
  {
    id: "tour_general",
    title: "Aurethion Crown in 8 clicks",
    subtitle: "The whole app, explained like you're 8.",
    audience: "any_admin",
    ownerOnly: false,
    testId: "tut-general",
    steps: [
      {
        title: "1. Command Center — your front porch",
        why: "See every business you own on one screen. Green means good. Red means look.",
        nav: "/",
        highlightTestId: "nav-command",
        do: [
          "Look at the coloured cards. Each card is one business.",
          "Click any card to walk inside that business.",
        ],
      },
      {
        title: "2. Companies — the whole family",
        why: "Every business, division and program lives in a tree here.",
        nav: "/companies",
        highlightTestId: "nav-companies",
        do: [
          "Pick a company card to open its private dashboard.",
          "The little padlock means the business is 'restricted' (holdings-only).",
        ],
      },
      {
        title: "3. Approvals — the yes/no desk",
        why: "Money spent, contracts, and website changes stop here first.",
        nav: "/approvals",
        highlightTestId: "nav-approvals",
        do: [
          "Filter by status = pending.",
          "Read the title. Click Approve or Reject. Add a note.",
          "Big amounts auto-escalate to Owner CEO.",
        ],
      },
      {
        title: "4. Site Editor — change the website safely",
        why: "Edit words and pictures. Nothing goes live until you approve it.",
        nav: "/editor",
        highlightTestId: "nav-editor",
        do: [
          "Pick a page from the left.",
          "Edit any text block. Hit Save. That creates a new version.",
          "Rollback anytime with one click.",
        ],
      },
      {
        title: "5. Fix Center — anyone can report a problem",
        why: "Employees, customers, and admins can drop a bug or wrong price here.",
        nav: "/fix-center",
        highlightTestId: "nav-fix",
        do: [
          "Read the newest reports at the top.",
          "Change status: reported → triaged → in progress → resolved.",
        ],
      },
      {
        title: "6. KPI & Risk — traffic lights for the business",
        why: "One page tells you what is healthy and what is on fire.",
        nav: "/kpi-risk",
        highlightTestId: "nav-kpi",
        do: [
          "Green KPIs = safe. Yellow = watch. Red = act today.",
          "Add a risk when you spot one; assign an owner and severity.",
        ],
      },
      {
        title: "7. People — who can open which doors",
        why: "Add helpers. Give each one only the doors they need.",
        nav: "/admins",
        highlightTestId: "nav-admins",
        do: [
          "Invite an admin by email.",
          "Give them a role. Add specific business scopes.",
          "Never share your Owner CEO login.",
        ],
      },
      {
        title: "8. Vaelithon Reign — the AI in your pocket",
        why: "Ask a question in plain English; get a plan back in seconds.",
        nav: "/vaelithon",
        highlightTestId: "nav-vaelithon",
        do: [
          "Pick a business up top.",
          "Type: 'Analyze today's dispatch' or 'Draft a spring promo email.'",
          "If it needs approval, an item shows up on the Approvals desk.",
        ],
      },
    ],
  },

  {
    id: "tour_land_sale",
    title: "Land Sale Playbook — RSVP private",
    subtitle: "How to sell a parcel end to end with every legal box ticked.",
    audience: "owner_only",
    ownerOnly: true,
    testId: "tut-land-sale",
    steps: [
      {
        title: "1. Prep the property",
        why: "Before anyone sees the listing, know what you own.",
        do: [
          "Order a title search (find liens, easements, back taxes).",
          "Commission a boundary survey.",
          "Get a licensed appraisal or 3 comps.",
          "Photograph corners, road access, and any structures.",
        ],
        docs: [
          "Deed you currently hold (copy from county recorder)",
          "Latest property-tax bill",
          "Existing title-insurance policy (if any)",
          "Survey / plat map",
        ],
      },
      {
        title: "2. Authorize the sale",
        why: "The LLC (RSVP) must formally decide to sell.",
        do: [
          "Draft a member resolution or written consent authorizing the sale.",
          "Confirm the operating agreement allows real-estate disposition.",
          "Log the decision as an internal approval in Aurethion Crown.",
        ],
        docs: [
          "LLC operating agreement (relevant section)",
          "Signed member resolution / written consent",
          "Certificate of good standing (state)",
        ],
        createApproval: {
          type: "contract",
          title: "Authorize land sale (LLC resolution)",
          description: "Member resolution authorizing RSVP LLC to sell subject parcel.",
        },
      },
      {
        title: "3. Set the price and disclosures",
        why: "Price to sell; disclose everything the law requires.",
        do: [
          "Set list price using the appraisal and comps.",
          "Fill state-required property disclosures (defects, easements, wetlands).",
          "If applicable, prepare environmental disclosure (Phase I ESA).",
        ],
        docs: [
          "State-specific seller property disclosure form",
          "Environmental disclosure (if commercial or previously industrial)",
          "Lead-based-paint disclosure (only if pre-1978 structure)",
        ],
      },
      {
        title: "4. List / market / find a buyer",
        why: "Get eyes on the parcel; screen buyers before offers.",
        do: [
          "List on MLS via agent, or list-by-owner on land marketplaces.",
          "Pre-qualify buyers (proof of funds or lender preapproval).",
          "Keep all offers in writing.",
        ],
        docs: [
          "Listing agreement (if using a broker)",
          "Marketing photos / drone footage release",
        ],
      },
      {
        title: "5. Purchase & sale agreement",
        why: "The single most important document. Everything else references it.",
        do: [
          "Use a real-estate attorney or state-approved PSA template.",
          "Include: legal description, price, earnest money, contingencies, closing date.",
          "Both parties sign; keep an original.",
        ],
        docs: [
          "Executed Purchase & Sale Agreement (PSA)",
          "Legal description exhibit (from survey/deed)",
          "Any addenda (financing, inspection, seller-financing)",
        ],
        createApproval: {
          type: "contract",
          title: "Sign land Purchase & Sale Agreement",
          description: "Execute PSA with buyer per negotiated terms.",
        },
      },
      {
        title: "6. Earnest money + open title",
        why: "Buyer puts skin in the game; title company begins the closing file.",
        do: [
          "Buyer wires earnest money to escrow / title company.",
          "Open title with your chosen title/escrow agent.",
          "Order title commitment; clear any exceptions before closing.",
        ],
        docs: [
          "Earnest money receipt / escrow deposit confirmation",
          "Title commitment",
          "Payoff letters (if any liens remain)",
        ],
      },
      {
        title: "7. Due diligence period",
        why: "Buyer inspects; seller cures anything that must be fixed pre-close.",
        do: [
          "Give access for inspections (survey, environmental, zoning).",
          "Respond in writing to any repair or credit requests.",
          "Confirm all contingencies are waived or satisfied before the deadline.",
        ],
        docs: [
          "Inspection reports (received copies)",
          "Contingency-waiver signed by buyer",
          "Written repair credits / amendments",
        ],
      },
      {
        title: "8. Closing",
        why: "Sign, fund, record. The parcel changes hands.",
        do: [
          "Review the settlement statement (ALTA / HUD-1 / CD) 24 hours ahead.",
          "Sign the deed transferring title to the buyer.",
          "Buyer wires funds; title company records the deed at the county.",
        ],
        docs: [
          "Signed Warranty Deed (or Special Warranty / Quitclaim per PSA)",
          "Settlement statement (ALTA/HUD-1/CD)",
          "Bill of sale (if personal property included)",
          "Affidavit of title / non-foreign (FIRPTA)",
          "1099-S issued by closing agent (or by seller if self-closing)",
          "State/county transfer-tax declaration",
        ],
        createApproval: {
          type: "document",
          title: "Approve final closing package",
          description: "Review and approve deed, settlement statement, FIRPTA, and 1099-S before closing.",
        },
      },
      {
        title: "9. Post-closing — keep the paperwork tidy",
        why: "The sale is done, but three more chores protect you next April.",
        do: [
          "Save the recorded deed and settlement statement to the RSVP records folder.",
          "Log the sale in the RSVP books (basis, sale price, capital gain).",
          "Confirm the 1099-S was filed with the IRS by the closing agent.",
          "Notify insurance to drop the property from the policy.",
        ],
        docs: [
          "Recorded deed (returned from county with recording stamp)",
          "Final closing package (all signed docs, PDF archive)",
          "Bookkeeping entry / journal for the sale",
          "1099-S IRS filing confirmation",
        ],
      },
    ],
    aiAssist: {
      // Uses the existing /api/v1/regalis/router endpoint (no new backend).
      request_type: "OPERATIONAL",
      industry_profile: "general_business",
      messageTemplate:
        "I am selling a parcel of land through my LLC (RSVP). " +
        "Details from me: {{userInput}}. " +
        "Return a customized step-by-step plan that starts from my current stage, " +
        "flags any legal or tax paperwork specific to my state, and marks any step " +
        "that should become a Aurethion Crown approval item.",
    },
  },

  {
    id: "tour_rental_turnover",
    title: "Rental Turnover Playbook",
    subtitle: "Move-out to move-in in seven safe steps.",
    audience: "any_admin",
    ownerOnly: false,
    testId: "tut-rental-turnover",
    steps: [
      {
        title: "1. Move-out inspection",
        why: "Catch damage while the tenant is still legally on the hook.",
        do: [
          "Walk the unit within 24 hours of key return.",
          "Photograph every room, floor, appliance, and fixture.",
          "Compare to move-in checklist; note any new damage.",
        ],
        docs: [
          "Signed move-in condition report (for comparison)",
          "Move-out inspection report with photos",
          "Utility final-reading photos",
        ],
      },
      {
        title: "2. Security deposit accounting",
        why: "State laws are strict — an itemized statement in writing avoids lawsuits.",
        do: [
          "Itemize damages beyond normal wear and tear with cost estimates.",
          "Deduct only actual amounts backed by receipts or written estimates.",
          "Send the statement + any refund within your state's deadline (often 21–30 days).",
        ],
        docs: [
          "Itemized security-deposit statement",
          "Repair receipts / contractor estimates",
          "Certified-mail return receipt",
        ],
        createApproval: {
          type: "expense",
          title: "Approve turnover repair spend",
          description: "Approve deposit deductions and repair spend for this unit.",
        },
      },
      {
        title: "3. Deep clean and repairs",
        why: "A show-ready unit rents 40% faster.",
        do: [
          "Book cleaners; require 'move-in clean' checklist.",
          "Paint any wall with damage or scuffs.",
          "Replace air filters, smoke-detector batteries, and worn caulk.",
        ],
        docs: ["Cleaning invoice", "Contractor invoices", "Before/after photos"],
      },
      {
        title: "4. Habitability & compliance check",
        why: "You cannot list a unit that fails code — and you don't want to.",
        do: [
          "Test every smoke and CO detector.",
          "Confirm HVAC, water heater, and locks work.",
          "Refresh any state-required disclosures (lead paint, mold, etc.).",
        ],
        docs: [
          "Signed detector test log",
          "Lead-paint disclosure (pre-1978 units)",
          "State-specific habitability certificate (if required)",
        ],
      },
      {
        title: "5. Re-list at market",
        why: "Repricing every turnover captures rent growth without gouging.",
        do: [
          "Pull 3 comparable rentals within a half-mile.",
          "Update photos and description.",
          "Post to your syndication (Zillow, Apartments.com, etc.).",
        ],
        docs: ["Rent comparables sheet", "Listing screenshots"],
      },
      {
        title: "6. Screen and lease",
        why: "The wrong tenant costs more than a month vacant.",
        do: [
          "Run credit, criminal, and eviction background.",
          "Verify income (3x rent minimum recommended).",
          "Contact prior landlord.",
        ],
        docs: [
          "Signed rental application",
          "Background-check consent",
          "Signed lease + all addenda",
          "Move-in inspection report (empty unit, dated)",
        ],
        createApproval: {
          type: "contract",
          title: "Approve new lease",
          description: "Approve executed lease agreement for this unit.",
        },
      },
      {
        title: "7. Move-in day",
        why: "Set expectations while the tenant is excited, not annoyed.",
        do: [
          "Walk the unit together; both sign the move-in report.",
          "Deliver keys, gate remotes, and welcome sheet.",
          "Set up rent auto-pay and maintenance-request portal.",
        ],
        docs: [
          "Signed move-in condition report",
          "Key-receipt log",
          "Welcome packet acknowledgement",
        ],
      },
    ],
    aiAssist: {
      request_type: "OPERATIONAL",
      industry_profile: "general_business",
      messageTemplate:
        "I'm doing a rental turnover. Details: {{userInput}}. " +
        "Give me a customized timeline, estimated total cost, and flag any " +
        "steps that should become Aurethion Crown approval items.",
    },
  },

  {
    id: "tour_contractor_onboarding",
    title: "Contractor Onboarding Playbook",
    subtitle: "Bring on a new contractor without exposing yourself.",
    audience: "any_admin",
    ownerOnly: false,
    testId: "tut-contractor-onboarding",
    steps: [
      {
        title: "1. Scope the work",
        why: "A vague scope = an inflated invoice.",
        do: [
          "Write a one-page scope of work (SOW) with deliverables and dates.",
          "Attach photos, drawings, or specs.",
          "Include start date, deadline, and change-order rules.",
        ],
        docs: ["Signed Scope of Work (SOW)", "Photos / specifications"],
      },
      {
        title: "2. Verify the contractor",
        why: "Uninsured contractors turn into your legal problem.",
        do: [
          "Confirm active state license (where required).",
          "Ask for Certificate of Insurance — general liability + workers' comp.",
          "List your LLC as an additional insured on the COI.",
        ],
        docs: [
          "Contractor license copy",
          "Certificate of Insurance (COI) naming us as additional insured",
          "Workers' comp certificate",
          "W-9 (for 1099 issuance)",
        ],
      },
      {
        title: "3. Bid comparison",
        why: "Always get at least two written bids for anything over $500.",
        do: [
          "Request itemized bids covering the same SOW.",
          "Compare labor vs materials line by line.",
          "Reject vague or 'lump sum' bids without breakdown.",
        ],
        docs: ["Two or more written bids", "Bid comparison worksheet"],
        createApproval: {
          type: "vendor",
          title: "Approve contractor selection",
          description: "Approve chosen contractor after bid comparison.",
        },
      },
      {
        title: "4. Master Services Agreement",
        why: "One MSA per contractor, then a Work Order per job. Saves time forever.",
        do: [
          "Sign a Master Services Agreement (MSA) covering liability, IP, payment terms, and dispute resolution.",
          "Attach a job-specific Work Order or SOW as an exhibit.",
        ],
        docs: [
          "Signed MSA",
          "Signed Work Order / SOW exhibit",
          "Indemnification clause reviewed",
        ],
        createApproval: {
          type: "contract",
          title: "Approve contractor MSA",
          description: "Approve master services agreement with new contractor.",
        },
      },
      {
        title: "5. Payment terms",
        why: "Never pay 100% up front. Ever.",
        do: [
          "Use draw schedule: 25% start, 50% mid-milestone, 25% on completion.",
          "Withhold final draw until walk-through is signed.",
          "Route all payments through the LLC — never personal accounts.",
        ],
        docs: ["Draw schedule attached to Work Order", "Payment receipts / bank memos"],
      },
      {
        title: "6. Lien waivers",
        why: "Without them, a subcontractor can lien your property.",
        do: [
          "Require conditional lien waivers with each progress payment.",
          "Require an unconditional final lien waiver before final payment.",
          "Store copies with the project file.",
        ],
        docs: [
          "Conditional progress lien waivers (per payment)",
          "Unconditional final lien waiver",
        ],
      },
      {
        title: "7. Closeout",
        why: "The last 5% of the job is 90% of your future risk.",
        do: [
          "Walk the finished work; make a punch list.",
          "Collect all warranties, manuals, and as-builts.",
          "Issue 1099-NEC by Jan 31 for any contractor paid ≥ $600 that year.",
        ],
        docs: [
          "Signed punch-list completion",
          "Warranty certificates + manuals",
          "1099-NEC (or e-file confirmation)",
        ],
      },
    ],
    aiAssist: {
      request_type: "OPERATIONAL",
      industry_profile: "general_business",
      messageTemplate:
        "I'm onboarding a contractor for the following job: {{userInput}}. " +
        "Give me a customized checklist, flag any red flags in what I described, " +
        "and mark any step that should become a Aurethion Crown approval item.",
    },
  },

  {
    id: "tour_mold_remediation",
    title: "Mold Remediation Playbook",
    subtitle: "Safe, code-correct remediation from first call to clearance.",
    audience: "any_admin",
    ownerOnly: false,
    testId: "tut-mold-remediation",
    steps: [
      {
        title: "1. Customer intake & safety triage",
        why: "Some mold jobs are medical emergencies. Sort them fast.",
        do: [
          "Ask: source (leak, flood, humidity), age, and any respiratory symptoms.",
          "If active water intrusion, dispatch water-mitigation team FIRST.",
          "Document any vulnerable occupants (infants, elderly, immunocompromised).",
        ],
        docs: [
          "Customer intake form",
          "Photos of visible growth and source",
          "Signed access authorization",
        ],
      },
      {
        title: "2. Assessment & scope",
        why: "IICRC S520 requires assessment BEFORE remediation.",
        do: [
          "Perform visual + moisture-meter survey.",
          "Categorize condition: Condition 1 (normal), 2 (settled spores), 3 (actual growth).",
          "Bring in an Indoor Environmental Professional (IEP) if area > 10 sq ft.",
        ],
        docs: [
          "Moisture map / meter readings",
          "IEP assessment report (if applicable)",
          "Air-quality baseline sample (recommended)",
        ],
      },
      {
        title: "3. Written remediation plan & estimate",
        why: "No customer signature = no work, no payment. Ever.",
        do: [
          "Write the plan: containment, PPE, HEPA, disposal, drying, clearance.",
          "Itemize labor, materials, equipment days, and IEP fees.",
          "Explain what insurance typically covers vs excludes.",
        ],
        docs: [
          "Written remediation plan (IICRC S520 compliant)",
          "Signed estimate & work authorization",
          "Insurance-coverage acknowledgement",
        ],
        createApproval: {
          type: "contract",
          title: "Approve mold remediation contract",
          description: "Approve signed work authorization and scope with customer.",
        },
      },
      {
        title: "4. Containment & PPE setup",
        why: "Cross-contamination is the #1 mold-lawsuit driver.",
        do: [
          "Erect Level 2 or 3 containment with 6-mil poly and zipper doors.",
          "Set up negative-air machines (HEPA) with 4+ air changes per hour.",
          "PPE: N95/P100 respirators, tyvek suits, gloves, eye pro.",
        ],
        docs: [
          "Containment photos before/after",
          "Air-scrubber log (start, run hours, filter changes)",
          "PPE issuance log",
        ],
      },
      {
        title: "5. Removal & cleaning",
        why: "Follow the S520 hierarchy: correct → contain → remove → clean → dry.",
        do: [
          "Physically remove porous materials with visible growth (drywall, insulation, carpet pad).",
          "HEPA-vacuum then damp-wipe non-porous surfaces with antimicrobial only after physical removal.",
          "Double-bag debris and gooseneck-seal before removing through containment.",
        ],
        docs: [
          "Waste manifest / disposal receipts",
          "Product Safety Data Sheets (SDS) for chemicals used",
          "Daily work log with photos",
        ],
      },
      {
        title: "6. Drying & structural repair prep",
        why: "Regrowth in 48 hours if moisture stays.",
        do: [
          "Run dehumidifiers + air movers until wood MC < 15% and drywall MC < 1%.",
          "Log moisture readings twice daily.",
          "Hand off to build-back only after readings are stable.",
        ],
        docs: [
          "Moisture-log signed each visit",
          "Handoff-to-rebuild sign-off",
        ],
      },
      {
        title: "7. Clearance testing",
        why: "Third-party clearance is what protects you legally.",
        do: [
          "Independent IEP runs post-remediation air/surface samples.",
          "Compare results to baseline / outdoor control.",
          "Do NOT remove containment until the IEP issues a written pass.",
        ],
        docs: [
          "Third-party clearance letter",
          "Post-remediation lab report",
          "Final walk-through photos",
        ],
        createApproval: {
          type: "document",
          title: "Approve clearance & job close-out",
          description: "Approve clearance report and close mold-remediation job.",
        },
      },
      {
        title: "8. Customer close-out & warranty",
        why: "The last impression is the referral engine.",
        do: [
          "Walk the customer through the finished space with the clearance report in hand.",
          "Deliver a written 1-year workmanship warranty (excludes new water events).",
          "Ask for a review or referral.",
        ],
        docs: [
          "Customer close-out packet (plan, log, clearance)",
          "Signed warranty acknowledgement",
          "Final invoice + payment receipt",
        ],
      },
    ],
    aiAssist: {
      request_type: "OPERATIONAL",
      industry_profile: "restoration",
      messageTemplate:
        "I'm scoping a mold job. Details: {{userInput}}. " +
        "Give me a customized S520-aligned plan, equipment list with day-count, " +
        "estimated total cost, and flag any step that should be a Aurethion Crown approval item.",
    },
  },
];

export const findTutorial = (id) => TUTORIALS.find((t) => t.id === id);
